import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { SaleActionsService } from '../services/sale-actions.service'
import { SaleType } from '../ngrx-store/app.state'
import { loadSavedSales } from '../ngrx-store/actions/corp-member.actions'
import { selectSavedSaleIds } from '../ngrx-store/selectors/corp.selectors'
import { conditionLabel, leavingLabel, nearness, thumbnailUrl } from '../utils/sale.util'

/** A sale in a listing grid (the /sales page, search, the home feed, the dashboard). */
@Component({
    selector: 'app-feed-sale-content',
    templateUrl: './feed-sale-content.component.html',
    styleUrls: ['./feed-sale-content.component.css'],
})
export class FeedSaleContentComponent implements OnInit, OnDestroy {
    constructor(
        private callerService: CallerService,
        public saleActions: SaleActionsService,
    ) {}

    @Input() sale!: SaleType

    /** Mark as sold / Delete on your own sale - where you manage your posts (the dashboard's Post history). */
    @Input() ownerActions = false

    private savedIds = new Set<number>()
    isSaving = false
    isUpdatingSold = false
    /** The cover photo didn't load - show the placeholder instead. */
    coverFailed = false

    private destroy$ = new Subject<void>()

    ngOnInit(): void {
        // Loads once per login, however many cards ask (see the effect).
        this.callerService.store.dispatch(loadSavedSales())
        this.callerService.store
            .select(selectSavedSaleIds)
            .pipe(takeUntil(this.destroy$))
            .subscribe((ids) => (this.savedIds = ids))
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    get saved(): boolean {
        return this.savedIds.has(this.sale.id)
    }

    get isSold(): boolean {
        return Boolean(this.sale?.sold_at)
    }

    get isOwn(): boolean {
        return this.saleActions.isOwnSale(this.sale)
    }

    get coverImage(): string | undefined {
        return this.sale?.Media?.[0]?.url
    }

    get coverThumbnail(): string | undefined {
        return thumbnailUrl(this.coverImage, 400)
    }

    /** Only for Cloudinary photos - other URLs have one size. */
    get coverSrcset(): string | null {
        const small = this.coverThumbnail
        return small && small !== this.coverImage ? `${small} 400w, ${thumbnailUrl(this.coverImage, 800)} 800w` : null
    }

    get photoCount(): number {
        return this.sale?.Media?.length ?? 0
    }

    get condition(): string | undefined {
        return conditionLabel(this.sale?.condition)
    }

    get sellerName(): string | undefined {
        return this.sale?.CorpMember?.first_name ?? this.sale?.CorpMember?.nickname
    }

    /** Same LGA as you. */
    get isNearYou(): boolean {
        return this.callerService.isLoggedIn && nearness(this.sale?.CorpMember?._location, this.callerService.corpMember?._location) === 2
    }

    get leaving(): string | undefined {
        return leavingLabel(this.sale?.CorpMember?.service_year_end_date)
    }

    toggleSave(): void {
        this.isSaving = true
        this.saleActions.toggleSave(this.sale, this.saved).subscribe(() => (this.isSaving = false))
    }

    toggleSold(): void {
        this.isUpdatingSold = true
        this.saleActions.setSold(this.sale, !this.isSold).subscribe((soldAt) => {
            this.isUpdatingSold = false
            // Post history's copy is in the store; this card's input may be a copy that isn't.
            if (soldAt !== undefined) this.sale = { ...this.sale, sold_at: soldAt }
        })
    }
}
